import { Link, useLocation } from "react-router-dom";
import { AirplaneTilt, EnvelopeSimple } from "@phosphor-icons/react";
import { pushEvent, currentPagePath } from "../../lib/analytics";

const POLICIES = [
  { label: "Terms & Conditions", to: "/terms" },
  { label: "Privacy Policy", to: "/privacy-policy" },
  { label: "Cancellation & Refund Policy", to: "/cancellation-refund-policy" },
  { label: "Shipping / Service Movement Policy", to: "/shipping-service-policy" },
  { label: "Pricing Details", to: "/pricing" },
  { label: "Contact Us", to: "/contact" },
];

const QUICK = [
  { label: "Track Shipment", to: "/track-my-shipment" },
  { label: "Priority Routes", to: "/priority-routes" },
];

function Col({ title, children }) {
  return (
    <div>
      <div className="overline mb-3">{title}</div>
      <ul className="space-y-2">{children}</ul>
    </div>
  );
}

function FLink({ to, label, testid, onClick }) {
  return (
    <li>
      <Link
        to={to}
        className="text-sm text-[hsl(var(--muted))] hover:text-[hsl(var(--accent))] transition-colors"
        data-testid={testid}
        onClick={onClick}
      >
        {label}
      </Link>
    </li>
  );
}

export default function PublicFooter() {
  const { pathname } = useLocation();
  // Parked interim homepage is `/interim`; the real homepage is `/` (and `/login`). Footer nav is
  // contextual:
  //  • Sign In: hidden on the parked interim page; everywhere else → real Sender sign-in at `/login#signin`.
  //  • Check Price: stay on the current page's homepage drawer (`/interim#check-price` on the parked
  //    page, local `#check-price` on `/login`, else `/#check-price`).
  const isInterimHome = pathname === "/interim";
  const checkPriceTo = pathname === "/interim"
    ? "/interim#check-price"
    : pathname === "/login" ? "/login#check-price" : "/#check-price";
  return (
    <footer id="public-footer" className="bg-[hsl(var(--background))] border-t" data-testid="public-footer">
      <div className="max-w-7xl mx-auto px-5 md:px-10 py-12 grid grid-cols-2 md:grid-cols-4 gap-8">
        <div className="col-span-2 md:col-span-1">
          <div className="flex items-center gap-2.5 mb-3">
            <span className="grid place-items-center w-8 h-8 bg-[hsl(var(--foreground))] text-white rounded-[2px]">
              <AirplaneTilt size={18} weight="bold" />
            </span>
            <span className="heading text-lg tracking-tight">AIR12</span>
          </div>
          <p className="text-sm font-medium">Priority air cargo. Made affordable.</p>
          <p className="text-sm text-[hsl(var(--muted))] mt-2 leading-relaxed">
            Urgent business cargo movement by air on Air12 Priority Routes.
          </p>
        </div>

        <Col title="Quick Links">
          <FLink to={checkPriceTo} label="Check Price" testid="footer-check-price" onClick={() => pushEvent("check_price_click", { page_path: currentPagePath(), placement: "footer", cta_name: "check_price" })} />
          {QUICK.map((q) => (
            <FLink key={q.label} to={q.to} label={q.label} testid={`footer-quick-${q.label.toLowerCase().replace(/[^a-z]+/g, "-")}`} />
          ))}
          {!isInterimHome && (
            <FLink to="/login#signin" label="Sign In" testid="footer-quick-sign-in" />
          )}
        </Col>

        <Col title="Policies">
          {POLICIES.map((p) => (
            <FLink key={p.label} to={p.to} label={p.label} testid={`footer-policy-${p.to.slice(1)}`} />
          ))}
        </Col>

        <Col title="Contact">
          <li className="text-sm text-[hsl(var(--muted))] flex items-center gap-2">
            <EnvelopeSimple size={14} weight="bold" />
            <a href="mailto:info@air12.in" className="hover:text-[hsl(var(--accent))]" data-testid="footer-email">info@air12.in</a>
          </li>
          <li className="text-sm text-[hsl(var(--muted))] leading-relaxed pt-1">
            Ascribe Attributions Systems Private Limited
          </li>
          <li className="text-sm text-[hsl(var(--muted))] leading-relaxed">
            Lightbridge, 6th Flr, Powai, Hiranandani Business Park, Mumbai 400072
          </li>
        </Col>
      </div>

      <div className="border-t">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-4 flex flex-col sm:flex-row gap-2 sm:items-center sm:justify-between">
          <p className="overline">© {new Date().getFullYear()} AIR12 · Ascribe Attributions Systems Private Limited</p>
          <div className="flex flex-col sm:flex-row gap-2 sm:items-center sm:gap-4">
            <p className="text-xs text-[hsl(var(--muted))]">
              Cargo insurance is mandatory and separate from the Air12 service price.
            </p>
            <Link to="/staff/login"
               className="text-xs text-[hsl(var(--muted))] underline underline-offset-2 hover:text-[hsl(var(--fg))] whitespace-nowrap"
               data-testid="footer-staff-login">
              Staff login
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
